import React, { Fragment, useEffect, useState } from "react";
import AppIcon from "ui/components/icons/AppIcon";
import { iconChevronRight } from "ui/components/icons";
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { OperatorSelect } from "./OperatorSelect";
import { DateRangePicker } from "./DateRangePicker";
import { MultiSelectFilter } from "./MultiSelectFilter";
import { Filter, GlobalSearchSelectOption } from "data/@types/GlobalSearchData";
import { BooleanFilter } from "./BooleanFilter";
import { CheckboxOptionsFilter } from "./CheckboxOptionsFilter";
import { NumericIdFilter } from "./NumericIdFilter";
import { GridColumnVisibilityModel } from "@mui/x-data-grid";
import {
  AdvancedViewFilterObject,
  useAdvancedViewFiltersStore,
} from "data/store/global-search/AdvancedViewFiltersStore";

export type AdvancedViewFiltersProps = {
  filters: Filter[];
  columnsVisibility: GridColumnVisibilityModel;
  activityType: GlobalSearchSelectOption;
};

export const AdvancedViewFilters = ({
  filters,
  columnsVisibility,
  activityType,
}: AdvancedViewFiltersProps) => {
  const [openFilters, setOpenFilters] = useState(
    {} as AdvancedViewFilterObject,
  );

  const activeFilters = useAdvancedViewFiltersStore(
    (state) => state.filtersByActivity[activityType] || {},
  );

  const handleToggleFilter = (filterLabel: string) => {
    setOpenFilters(
      (prevOpenFilters) =>
        ({
          ...prevOpenFilters,
          [filterLabel]: !prevOpenFilters[filterLabel],
        }) as AdvancedViewFilterObject,
    );
  };

  useEffect(() => {
    setOpenFilters(() => {
      const validData = Object.fromEntries(
        Object.entries(activeFilters).filter(([, obj]) => obj),
      );

      return {
        ...validData,
      };
    });
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      <List>
        {filters
          .filter(({ field }) => columnsVisibility[field])
          .map((filter) => (
            <Fragment key={filter.label}>
              <ListItemButton
                key={filter.label}
                onClick={() => handleToggleFilter(filter.label)}
                sx={{ mb: 1 }}
              >
                <AppIcon
                  icon={iconChevronRight}
                  fontSize="small"
                  sx={{
                    fontSize: 12,
                    mr: 1,
                    transform: openFilters[filter.label]
                      ? "rotate(90deg)"
                      : "none",
                    transition: "all ease-in-out 0.2s",
                  }}
                />
                <ListItemText
                  primaryTypographyProps={{
                    fontSize: 12,
                    textTransform: "uppercase",
                  }}
                  primary={filter.label}
                />
              </ListItemButton>
              <Collapse
                key={`${filter.label}-collapse`}
                in={Boolean(openFilters[filter.label])}
                timeout="auto"
                unmountOnExit
              >
                {filter.type === "text" && (
                  <OperatorSelect filter={filter} activityType={activityType} />
                )}

                {filter.type === "numeric" && (
                  <NumericIdFilter
                    filter={filter}
                    activityType={activityType}
                  />
                )}

                {filter.type === "daterange" && (
                  <DateRangePicker
                    label={filter.label}
                    activityType={activityType}
                  />
                )}

                {filter.type === "boolean" && (
                  <BooleanFilter
                    name={filter.label}
                    activityType={activityType}
                  />
                )}

                {(filter.type === "select" ||
                  filter.type === "simple-select") && (
                  <MultiSelectFilter<string>
                    id={filter.label}
                    label={filter.label}
                    fetchOptions={filter.fetchOptions}
                    editMode
                    simpleSelect={filter.type === "simple-select"}
                    simpleOptions={filter.options}
                    activityType={activityType}
                  />
                )}

                {filter.type === "checkbox-options" &&
                  Array.isArray(filter.options) &&
                  filter.options.length > 0 && (
                    <CheckboxOptionsFilter
                      label={filter.label}
                      options={filter.options}
                      activityType={activityType}
                    />
                  )}
              </Collapse>
            </Fragment>
          ))}
      </List>
    </Box>
  );
};
